import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import { CONTACT, STUDIO } from "@/lib/siteData";

// The design colours the header per page: orange with a white logotype over the
// home's orange hero, white with an orange logotype on the service pages
// (25:22 / 25:13). While the menu is open it is white with an orange logotype and
// a black icon (30:262).
//
// Two rules keep it from ever vanishing, which is what happened when it was
// transparent and the white logotype met a white section:
//
//  1. The bar always paints an opaque surface, and the logotype colour is chosen
//     against that surface — never against whatever scrolls underneath.
//  2. The white variant adds a hairline once the page scrolls, so it stays
//     distinguishable from white content sitting right below it. The orange
//     variant does not need one; it separates by colour alone.
const TONES = {
    brand: { surface: "bg-brand", logo: "text-paper", bar: "bg-paper", icon: "text-paper", needsHairline: false },
    paper: { surface: "bg-paper", logo: "text-brand", bar: "bg-brand", icon: "text-brand", needsHairline: true },
    dark: { surface: "bg-[#181818]", logo: "text-paper", bar: "bg-paper", icon: "text-paper", needsHairline: false },
    contactBg: { surface: "bg-transparent", logo: "text-paper", bar: "bg-paper", icon: "text-paper", needsHairline: false },
    menuOpen: { surface: "bg-paper", logo: "text-brand", bar: "bg-ink", icon: "text-ink", needsHairline: false },
};

const InstagramIcon = ({ className = "h-[25px] w-[25px]" }) => (
    <svg
        viewBox="0 0 16 16"
        fill="currentColor"
        className={className}
        aria-hidden="true"
        focusable="false"
    >
        <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598 2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334" />
    </svg>
);

export const Navbar = ({ tone = "paper", isMenuOpen, onToggleMenu }) => {
    const [isScrolled, setIsScrolled] = useState(false);

    // contactBg stays transparent only over the bubble background at the very
    // top of the contact page. Once the visitor scrolls, the steps card (taller
    // than the viewport) passes underneath this fixed header — being
    // transparent, the header's own logo/icon rendered on top of the card's
    // title instead of an opaque bar covering it. Falling back to the "paper"
    // surface on scroll restores rule 1 above (the bar always paints something
    // opaque) for the one tone that opts out of it at rest.
    const effectiveTone = isMenuOpen ? "menuOpen" : tone === "contactBg" && isScrolled ? "paper" : tone;
    const { surface, logo, bar, icon, needsHairline } = TONES[effectiveTone] ?? TONES.paper;

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const hairline =
        needsHairline && isScrolled && !isMenuOpen ? "border-b border-ink/10" : "border-b border-transparent";

    return (
        <header
            data-testid={TID.navbar}
            data-tone={isMenuOpen ? "menuOpen" : tone}
            className={`fixed left-0 right-0 top-0 z-[90] transition-colors duration-300 ${surface} ${hairline}`}
        >
            <div className="mx-auto flex h-[100px] max-w-[1440px] items-center justify-between px-6 md:px-12">
                <Link
                    to="/"
                    data-testid={TID.navbarLogo}
                    onClick={() => isMenuOpen && onToggleMenu()}
                    aria-label={`${STUDIO.name}, ir para a home`}
                    className={`type-display text-[26px] leading-none transition-colors duration-300 md:text-display ${logo}`}
                >
                    {STUDIO.name}
                </Link>

                <div className="flex items-center gap-5 sm:gap-6">
                    <a
                        href={CONTACT.instagram.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={TID.navbarInstagram}
                        aria-label="Instagram Rock Fox Studio"
                        className={`-m-2 box-content flex h-[29px] w-[29px] items-center justify-center p-2 transition-all duration-200 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-current ${icon}`}
                    >
                        <InstagramIcon />
                    </a>

                    {/* box-content keeps the icon exactly 29x29 as drawn, while the
                        padding grows the tap area to 45x45 and the negative margin
                        cancels that padding in layout — so nothing moves on screen.
                        29px is a fine mouse target and a poor thumb one, and this
                        button is the only navigation the site has on a phone. */}
                    <button
                        type="button"
                        onClick={onToggleMenu}
                        data-testid={TID.navbarMenu}
                        aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
                        aria-expanded={isMenuOpen}
                        className="-m-2 box-content flex h-[29px] w-[29px] flex-col justify-center gap-[8px] p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
                    >
                        {/* Three 29px rules (17:8 / 17:62 / 17:63), collapsing into an X. */}
                        <motion.span
                            animate={{ rotate: isMenuOpen ? 45 : 0, y: isMenuOpen ? 11 : 0 }}
                            transition={{ duration: 0.3 }}
                            className={`block h-[3px] w-full origin-center transition-colors duration-300 ${bar}`}
                        />
                        <motion.span
                            animate={{ opacity: isMenuOpen ? 0 : 1 }}
                            transition={{ duration: 0.2 }}
                            className={`block h-[3px] w-full transition-colors duration-300 ${bar}`}
                        />
                        <motion.span
                            animate={{ rotate: isMenuOpen ? -45 : 0, y: isMenuOpen ? -11 : 0 }}
                            transition={{ duration: 0.3 }}
                            className={`block h-[3px] w-full origin-center transition-colors duration-300 ${bar}`}
                        />
                    </button>
                </div>
            </div>
        </header>
    );
};
